import type { EixoMedida, EncaixeTipo, EstadoMedir, MovelViewer } from '../three/viewerCore.js';
import { gravarBolinhaFixa } from '../lib/medir.js';

/** Como o marceneiro chama o lugar onde o ímã grudou. */
const ENCAIXE: Record<EncaixeTipo, string> = {
  canto: 'no canto',
  aresta: 'na aresta',
  meio: 'no meio da aresta',
  face: 'na face',
  planta: 'na planta',
};

/**
 * Cada botão X/Y/Z leva a cor do eixo (classe eixo-x/y/z no CSS): a mesma da
 * bússola e das setas no ponto, para a pessoa ligar o botão à seta na tela.
 *
 * Eixos com o nome de quem está na fábrica: o mundo do glTF é Y-up (o plugin
 * converte o Z-up do SketchUp), então X é largura, Y altura e Z profundidade.
 */
const EIXOS: Array<[EixoMedida, string, string]> = [
  ['livre', 'Livre', 'Distância reta entre os dois pontos'],
  ['x', 'X', 'X largura — mede só de lado a lado'],
  ['y', 'Y', 'Y altura — mede só de baixo para cima'],
  ['z', 'Z', 'Z profundidade — mede só da frente para o fundo'],
];

/**
 * Barra flutuante do "Medir 2 pontos": so aparece com o modo ligado (nao polui a
 * tela) e fica EM CIMA do 3D — no tablet a barra lateral vira gaveta fechada,
 * entao os botoes de marcar/desfazer precisam estar a mao.
 */
export function BarraMedir({ viewer, estado, onConcluir }: {
  viewer: MovelViewer | null;
  estado: EstadoMedir;
  onConcluir: () => void;
}) {
  const { fixa, mira, pendente, distancia, medidas, encaixe, eixo, componentes } = estado;

  // "512 mm (X 500 · Z 110)": a reta sozinha mente quando os dois cliques ficam
  // quase alinhados — os componentes por eixo mostram a folga de verdade.
  const medida = distancia && componentes ? `${distancia} (${componentes})` : distancia;

  let texto: string;
  if (fixa) {
    if (!pendente) texto = mira
      ? 'Arraste a bolinha até o ponto (ou toque em outro lugar) e marque o 1º ponto.'
      : 'Toque onde quer medir: a bolinha fica parada lá.';
    else texto = mira
      ? `Até a bolinha: ${medida}. Ajuste e marque o 2º ponto.`
      : '1º ponto marcado. Toque onde fica o 2º ponto.';
  } else {
    texto = pendente
      ? (medida ? `Até o cursor: ${medida}. Clique no 2º ponto.` : 'Clique no 2º ponto.')
      : 'Clique no 1º ponto.';
  }

  function alternarFixa(on: boolean) {
    gravarBolinhaFixa(on);
    viewer?.setBolinhaFixa(on);
  }

  return (
    <div className="vw-medir" role="toolbar" aria-label="Medir 2 pontos">
      <div className="vw-medir-txt" aria-live="polite">
        {texto}
        {encaixe && <span className={`vw-medir-encaixe e-${encaixe}`}>🧲 {ENCAIXE[encaixe]}</span>}
      </div>
      <div className="vw-medir-eixos" role="group" aria-label="Travar eixo da medida">
        <span className="vw-medir-eixos-rot">Travar eixo:</span>
        {EIXOS.map(([id, rotulo, dica]) => (
          <button key={id} type="button" title={dica} aria-label={dica} aria-pressed={eixo === id}
            className={`vw-medir-eixo eixo-${id}${eixo === id ? ' ativo' : ''}`}
            onClick={() => viewer?.setEixoMedida(id)}>
            {rotulo}
          </button>
        ))}
      </div>
      <div className="vw-medir-botoes">
        {fixa && (
          <button type="button" className="primary vw-medir-marcar" disabled={!mira} onClick={() => viewer?.marcarPonto()}>
            📍 Marcar {pendente ? '2º' : '1º'} ponto
          </button>
        )}
        <button type="button" className="vw-medir-desfazer" disabled={!pendente && medidas === 0}
          onClick={() => viewer?.desfazerPonto()}>
          ↩ Desfazer
        </button>
        <button type="button" className="vw-medir-concluir" onClick={onConcluir}>✓ Concluir</button>
      </div>
      <label className="vw-medir-fixa">
        <input type="checkbox" checked={fixa} onChange={(e) => alternarFixa(e.target.checked)} />
        <span>Bolinha fixa <span className="muted small">— posiciona primeiro, marca depois</span></span>
      </label>
    </div>
  );
}
